const __r = (function (r: any): any { return arguments[0]; })(require);
import { CreativeDataNode } from './nodes';
import * as N from './nodes';
import { Color, RGBA_COLOR_PATTERN } from './color';
import { IKeyframe } from '../domain/keyframe';
import { IPadding, IBorder, IShadow, ITextShadow } from '../domain/style';
import { IFontStyle } from '../domain/font';
import { serializeVersionedText, deserializeVersionedText, getHashFromStyle, serializeInlineStyledText } from './text-nodes';
import { IFeed } from '../domain/feed';
import { IVersionProperty } from '../domain/version';
import { ICharacterStylesMap, IVariable } from '../domain/text';
import { createMapFromArray, decimal, cloneDeep } from '../../lib/utils';
import * as value from './serializer.value';
import { schema } from './serializer.schema';
import { OneOfMethod } from '../data/datastore';
import { isUrl } from '../ad/utils/url';
// import * as Ajv from 'ajv';
let Ajv;
if (process.env.STUDIO_JS) {
    Ajv = require('ajv');
}


interface ISerializedGuideline {
    id: string;
    position: {
        x: number;
        y: number;
    };
    type: 'horizontal' | 'vertical';
    preview: boolean;
}

interface ISerializedPreloadImage {
    frames: number[];
    quality: number;
    format: 'jpg' | 'gif' | 'png';
}

interface ISerializedCreative {
    id: string;
    width: number;
    height: number;
    fill: string;
    radius: number;
    guidelines: ISerializedGuideline[];
    elements: OneOfSerializedElement[];
    loops?: number;
    startTime?: number;
    stopTime?: number;
    preloadImage: ISerializedPreloadImage;
}

interface ISerializedHead {
    asset: {
        images: ISerializedImageAsset[];
    };
    elements: ISerializedElementIdType[];
}

export interface ISerializedDocument {
    head: ISerializedHead;
    body: {
        creative: ISerializedCreative;
    };
}

function deserializeTextLikeElement(dataElement: N.OneOfTextDataNodes, serializedTextLikeElement: ISerializedTextLikeElement): void {
    dataElement.fontSize = deserializeKeyframes(serializedTextLikeElement.fontSize, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.lineHeight = deserializeKeyframes(serializedTextLikeElement.lineHeight, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.characterSpacing = deserializeKeyframes(serializedTextLikeElement.characterSpacing, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.textColor = deserializeKeyframes(serializedTextLikeElement.textColor, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.underline = deserializeKeyframes(serializedTextLikeElement.underline, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.uppercase = deserializeKeyframes(serializedTextLikeElement.uppercase, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.strikethrough = deserializeKeyframes(serializedTextLikeElement.strikethrough, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.maxRows = deserializeKeyframes(serializedTextLikeElement.maxRows, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.padding = deserializeKeyframes(serializedTextLikeElement.padding, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.textOverflow = deserializeKeyframes(serializedTextLikeElement.textOverflow, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.horizontalAlignment = deserializeKeyframes(serializedTextLikeElement.horizontalAlignment, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    dataElement.verticalAlignment = deserializeKeyframes(serializedTextLikeElement.verticalAlignment, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    if (serializedTextLikeElement.textShadows) {
        dataElement.textShadows = deserializeKeyframes(serializedTextLikeElement.textShadows, serializedTextLikeElement.inTransition, serializedTextLikeElement.outTransition);
    }

    const characterStyles: ICharacterStylesMap = new Map<string, Partial<N.ICharacterProperties>>();
    const styleHashMap = new Map</*styleHash*/string, /*styleId*/string>();
    for (const characterStyle of serializedTextLikeElement.characterStyles) {
        const id = characterStyle.id;
        const style = deserializeStyle2(characterStyle.value);
        styleHashMap.set(getHashFromStyle(style), id);
        characterStyles.set(id, style);
    }
    dataElement.characterStyles = characterStyles;
    dataElement.__deletedStyleHashMap = new Map</*styleHash*/string, /*styleId*/string>();
    dataElement.__styleHashMap = styleHashMap;
    dataElement.__fontStyleId = serializedTextLikeElement.font;
}

export function deserializeDesignDocument(document: string, oldCreative?: N.CreativeDataNode): N.CreativeDataNode {
    if (oldCreative && oldCreative.destroy) {
        oldCreative.destroy();
    }
    const serializedDocument = JSON.parse(document) as ISerializedDocument;
    const serializedCreative = serializedDocument.body.creative;
    const creative = oldCreative || new N.CreativeDataNode({
        id: serializedCreative.id,
        width: serializedCreative.width,
        height: serializedCreative.height,
        loops: serializedCreative.loops,
        radius: serializedCreative.radius,
        startTime: serializedCreative.startTime,
        stopTime: serializedCreative.stopTime,
        guidelines: serializedCreative.guidelines,
        fill: Color.parse(serializedCreative.fill),
        preloadImage: serializedCreative.preloadImage,
    });
    creative.elements = [];
    for (const serializedElement of serializedDocument.body.creative.elements) {
        const element = {} as N.IElementDataNode;
        if ((serializedElement as ISerializedTextElement).__textKind) {
            element.kind = N.NodeKind.Text;
            deserializeTextLikeElement(element as N.ITextElementDataNode, serializedElement as ISerializedTextElement);
        }
        else if ((serializedElement as ISerializedButtonElement).__buttonKind) {
            element.kind = N.NodeKind.Button;
            deserializeTextLikeElement(element as N.IButtonElementDataNode, serializedElement as ISerializedButtonElement);
        }
        else if ((serializedElement as ISerializedRectangleElement).__rectangleKind) {
            element.kind = N.NodeKind.Rectangle;
        }
        else if ((serializedElement as ISerializedEllipseElement).__ellipseKind) {
            element.kind = N.NodeKind.Ellipse;
        }
        else if ((serializedElement as ISerializedImageElement).__imageKind) {
            element.kind = N.NodeKind.Image;
            const serializedImageElement = serializedElement as ISerializedImageElement;
            const imageAsset = serializedDocument.head.asset.images.find((i) => i.id === serializedImageElement.imageAssetId)!;
            if (imageAsset) {
                element.imageAsset = {
                    id: imageAsset.id,
                    url: imageAsset.src,
                    width: imageAsset.width,
                    height: imageAsset.height // TODO: Clean up url to be src.
                };
            }
            element.feed = serializedImageElement.feed;
        }
        else if ((serializedElement as ISerializedWidgetElement).__widgetKind) {
            element.kind = N.NodeKind.Widget;
            const widget = element as N.IWidgetElementDataNode;
            const customProperties: N.IWidgetCustomProperty[] = [];
            for (const serializedCustomProperty of (serializedElement as ISerializedWidgetElement).customProperties) {
                customProperties.push(value.deserializeWidgetProperty(serializedCustomProperty));
            }
            widget.customProperties = customProperties;
        }
        deserializeElementProperties(element, serializedElement);
        element.__rootNode = creative;
        creative.elements.push(N.createDataElement(element as N.OneOfDataElements));
    }
    return creative;
}

function deserializeKeyframes(serializedKeyframes: ISerializedKeyframe<value.OneOfSerializedValue>[], inTransition: ISerializedTransition, outTransition: ISerializedTransition): IKeyframe[] {
    return serializedKeyframes.map((k) => {
        let value: any;
        switch (k.value.type) {
            case 'enum':
            case 'number':
            case 'boolean':
            case 'integer':
            case 'formula':
            case 'padding':
                value = k.value.value;
                break;
            case 'color':
                value = Color.parse(k.value.value);
                break;
            case 'border':
                const border = k.value.value;
                value = {
                    thickness: border.thickness,
                    type: border.style,
                    color: border.color,
                };
                break;
            case 'shadows':
                value = k.value.value.map((s) => {
                    return {
                        offsetX: s.offsetX,
                        offsetY: s.offsetY,
                        blur: s.blur,
                        spread: s.spread,
                        color: s.color,
                    };
                });
                break;
            case 'text-shadows':
                value = k.value.value.map((s) => {
                    return {
                        offsetX: s.offsetX,
                        offsetY: s.offsetY,
                        blur: s.blur,
                        color: s.color,
                    };
                });
                break;
            default:
                throw new Error(`Unknown type '${(k.value as any).type}'`);
        }
        const serializedTransition = [inTransition, outTransition].find(t => t !== undefined && t.template === k.transition);

        return {
            time: k.time,
            timingFunction: k.timingFunction || undefined,
            transition: serializedTransition && {
                type: (serializedTransition === inTransition ? 'in' : 'out') as 'in' | 'out',
                name: serializedTransition.name,
                timingFunction: serializedTransition.timingFunction,
                template: serializedTransition.template,
                settings: serializedTransition.settings && createMapFromArray(serializedTransition.settings, 'key') as { [key: string]: N.ITransitionSetting; }
            },
            value: value
        };
    });
}

function deserializeElementProperties(dataElement: N.IElementDataNode, serializedElement: ISerializedElement): void {
    dataElement.elementCid = '';
    dataElement.id = serializedElement.id;
    dataElement.time = serializedElement.time;
    dataElement.duration = serializedElement.duration;
    dataElement.parentId = serializedElement.parentId || undefined;
    dataElement.locked = serializedElement.locked;
    dataElement.hidden = serializedElement.hidden;
    dataElement.ratio = serializedElement.ratio || undefined;
    if (serializedElement.fill) {
        dataElement.fill = deserializeKeyframes(serializedElement.fill, serializedElement.inTransition, serializedElement.outTransition);
    }
    dataElement.opacity = deserializeKeyframes(serializedElement.opacity, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.x = deserializeKeyframes(serializedElement.x, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.y = deserializeKeyframes(serializedElement.y, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.width = deserializeKeyframes(serializedElement.width, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.height = deserializeKeyframes(serializedElement.height, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.radius = deserializeKeyframes(serializedElement.radius, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.originX = deserializeKeyframes(serializedElement.originX, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.originY = deserializeKeyframes(serializedElement.originY, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.rotationX = deserializeKeyframes(serializedElement.rotationX, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.rotationY = deserializeKeyframes(serializedElement.rotationY, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.rotationZ = deserializeKeyframes(serializedElement.rotationZ, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.scaleX = deserializeKeyframes(serializedElement.scaleX, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.scaleY = deserializeKeyframes(serializedElement.scaleY, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.mirrorX = deserializeKeyframes(serializedElement.mirrorX, serializedElement.inTransition, serializedElement.outTransition);
    dataElement.mirrorY = deserializeKeyframes(serializedElement.mirrorY, serializedElement.inTransition, serializedElement.outTransition);

    if (serializedElement.border) {
        dataElement.border = deserializeKeyframes(serializedElement.border, serializedElement.inTransition, serializedElement.outTransition);
    }
    if (serializedElement.shadows) {
        dataElement.shadows = deserializeKeyframes(serializedElement.shadows, serializedElement.inTransition, serializedElement.outTransition);
    }
    if (serializedElement.fitOption) {
        dataElement.fitOption = deserializeKeyframes(serializedElement.fitOption, serializedElement.inTransition, serializedElement.outTransition);
    }
    if (serializedElement.fitPositionX) {
        dataElement.fitPositionX = deserializeKeyframes(serializedElement.fitPositionX, serializedElement.inTransition, serializedElement.outTransition);
    }
    if (serializedElement.fitPositionY) {
        dataElement.fitPositionY = deserializeKeyframes(serializedElement.fitPositionY, serializedElement.inTransition, serializedElement.outTransition);
    }
    if (serializedElement.quality) {
        dataElement.quality = deserializeKeyframes(serializedElement.quality, serializedElement.inTransition, serializedElement.outTransition);
    }

    dataElement.constraint = {
        auto: serializedElement.constraint.auto,
        top: serializedElement.constraint.top,
        left: serializedElement.constraint.left,
        right: serializedElement.constraint.right,
        bottom: serializedElement.constraint.bottom,
        width: serializedElement.constraint.width,
        height: serializedElement.constraint.height,
        ratio: serializedElement.constraint.ratio,
        contained: serializedElement.constraint.contained,
    };
    if (serializedElement.inTransition) {
        dataElement.inTransition = createTransition('in', serializedElement.inTransition);
    }
    if (serializedElement.outTransition) {
        dataElement.outTransition = createTransition('out', serializedElement.outTransition);
    }
}

function createTransition(type: 'in' | 'out', serializedTransition: ISerializedTransition): N.ITransition {
    const name = serializedTransition.name;
    const template = serializedTransition.template;
    const timingFunction = serializedTransition.timingFunction;
    const settings = serializedTransition.settings;

    const transition: N.ITransition = {
        name: name ? name : 'Untitled',
        type: type,
        timingFunction: timingFunction ? timingFunction : undefined,
        template: template || undefined
    };

    if (settings) {
        const resultSettings = {};

        for (const setting of settings) {
            const key = setting.key;
            const name = setting.name;
            const value = setting.value;

            resultSettings[key] = {
                name: name,
                value: parseTransitionSettingValue(key, value)
            };
        }
        transition.settings = settings.length > 0 ? resultSettings : undefined;
    }

    return transition;
}

function parseTransitionSettingValue(setting: string, value: any): any {
    switch (N.transitionSettingPropertyToUnitMap[setting]) {
        case 'angle':
            return parseInt(value, 10);
        default:
            return value;
    }
}

interface ISerializedConstraint {
    auto: boolean;
    contained: boolean;
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
    width?: number;
    height?: number;
    ratio?: number;
}

interface ISerializedTransition {
    name: string;
    timingFunction: string;
    template: string;
    settings?: ISerializedTransitionSetting[];
}

interface ISerializedTransitionSetting {
    key: string;
    name: string;
    value: any;
}

interface ISerializedElement {
    id: string;
    time: number;
    duration: number;
    parentId: string | null;
    locked: boolean;
    hidden: boolean;
    fill?: ISerializedKeyframe<value.ISerializedColorValue>[];
    ratio: number | null;
    opacity: ISerializedKeyframe<value.ISerializedNumberValue>[];
    x: ISerializedKeyframe<value.ISerializedNumberValue>[];
    y: ISerializedKeyframe<value.ISerializedNumberValue>[];
    width: ISerializedKeyframe<value.ISerializedNumberValue>[];
    height: ISerializedKeyframe<value.ISerializedNumberValue>[];
    originX: ISerializedKeyframe<value.ISerializedNumberValue>[];
    originY: ISerializedKeyframe<value.ISerializedNumberValue>[];
    rotationX: ISerializedKeyframe<value.ISerializedNumberValue>[];
    rotationY: ISerializedKeyframe<value.ISerializedNumberValue>[];
    rotationZ: ISerializedKeyframe<value.ISerializedNumberValue>[];
    scaleX: ISerializedKeyframe<value.ISerializedNumberValue>[];
    scaleY: ISerializedKeyframe<value.ISerializedNumberValue>[];
    mirrorX: ISerializedKeyframe<value.ISerializedNumberValue>[];
    mirrorY: ISerializedKeyframe<value.ISerializedNumberValue>[];
    radius: ISerializedKeyframe<value.ISerializedNumberValue>[];
    shadows?: ISerializedKeyframe<value.ISerializedShadowsValue>[];
    border?: ISerializedKeyframe<value.ISerializedBorderValue>[];
    inTransition: ISerializedTransition;
    outTransition: ISerializedTransition;
    constraint: ISerializedConstraint;
    fitOption: ISerializedKeyframe<value.ISerializedEnumValue>[];
    fitPositionX: ISerializedKeyframe<value.ISerializedNumberValue>[];
    fitPositionY: ISerializedKeyframe<value.ISerializedNumberValue>[];
    quality: ISerializedKeyframe<value.ISerializedNumberValue>[];
}

interface ISerializedImageAsset {
    id: string;
    src: string;
    width?: number;
    height?: number;
}

interface ISerializedCharacterProperties {
    textColor?: string;
    fontSize?: number;
    font?: string;
    uppercase?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    characterSpacing: number;
    lineHeight: number;
    textShadows?: ISerializedTextShadow[];
    variable?: ISerializedFeed;
}


interface ISerializedTextShadow {
    offsetX: number;
    offsetY: number;
    blur: number;
    color: string;
}

interface ISerializedCharacterStyle {
    id: string;
    value: ISerializedCharacterProperties;
}

interface ISerializedTextLikeElement extends ISerializedElement {
    font: string;
    fontSize: ISerializedKeyframe<value.ISerializedNumberValue>[];
    lineHeight: ISerializedKeyframe<value.ISerializedNumberValue>[];
    characterSpacing: ISerializedKeyframe<value.ISerializedNumberValue>[];
    textColor: ISerializedKeyframe<value.ISerializedColorValue>[];
    underline: ISerializedKeyframe<value.ISerializedBooleanValue>[];
    uppercase: ISerializedKeyframe<value.ISerializedBooleanValue>[];
    strikethrough: ISerializedKeyframe<value.ISerializedBooleanValue>[];
    textOverflow: ISerializedKeyframe<value.ISerializedEnumValue>[];
    textShadows: ISerializedKeyframe<value.ISerializedTextShadowsValue>[];
    maxRows: ISerializedKeyframe<value.ISerializedIntegerValue>[];
    padding: ISerializedKeyframe<value.ISerializedPaddingValue>[];
    horizontalAlignment: ISerializedKeyframe<value.ISerializedEnumValue>[];
    verticalAlignment: ISerializedKeyframe<value.ISerializedEnumValue>[];
    characterStyles: ISerializedCharacterStyle[];
}

interface ISerializedTextElement extends ISerializedTextLikeElement {
    __textKind: true; // Needed for JSON Schema oneOf operator.
}

interface ISerializedButtonElement extends ISerializedTextLikeElement {
    __buttonKind: true; // Needed for JSON Schema oneOf operator.
}

interface ISerializedRectangleElement extends ISerializedElement {
    __rectangleKind: true; // Needed for JSON Schema oneOf operator.
}

interface ISerializedEllipseElement extends ISerializedElement {
    __ellipseKind: true; // Needed for JSON Schema oneOf operator.
}

interface ISerializedImageElement extends ISerializedElement {
    __imageKind: true; // Needed for JSON Schema oneOf operator.
    imageAssetId: string;
    feed: ISerializedFeed;
}

interface ISerializedWidgetElement extends ISerializedElement {
    __widgetKind: true; // Needed for JSON Schema oneOf operator.
    customProperties: value.ISerializedWidgetProperty[];
}

interface ISerializedFeedStep {
    start: number;
    size: number;
    occurrence: 'loop' | 'none';
}

export interface ISerializedFeed {
    id: string;
    path: string;
    step: ISerializedFeedStep;
    fallback: string;
    type: 'text' | 'number' | 'image';
}

type OneOfSerializedElement =
    ISerializedTextElement |
    ISerializedButtonElement |
    ISerializedRectangleElement |
    ISerializedEllipseElement |
    ISerializedImageElement |
    ISerializedWidgetElement;

interface ISerializedKeyframe<T extends value.OneOfSerializedValue> {
    time: number;
    value: T;
    timingFunction: string | null;
    transition: string | null;
}

function serializeValue(property: string, value: string | number | boolean | Color | IPadding | IBorder | IShadow<Color>[]): value.OneOfSerializedValue {
    const unit = N.propertyToUnitMap[property];
    switch (unit) {
        case 'enum':
            return { type: 'enum', value: value as string };
        case 'integer':
            return { type: 'integer', value: value as number };
        case 'number':
            if (typeof value === 'number') {
                return { type: 'number', value: decimal(value as number) };
            }
            else if (typeof value === 'string') {
                return { type: 'formula', value: value as string };
            }
            else {
                throw new Error('Unexpected number value.');
            }
        case 'boolean':
            return { type: 'boolean', value: value as boolean };
        case 'Shadow[]':
            const shadows = value as IShadow<Color>[];
            return { type: 'shadows', value: shadows.map(s => ({ offsetX: decimal(s.offsetX), offsetY: decimal(s.offsetY), blur: decimal(s.blur), spread: decimal(s.spread), color: s.color.toString() })) };
        case 'TextShadow[]':
            const textShadows = value as ITextShadow[];
            return { type: 'text-shadows', value: textShadows.map(s => ({ offsetX: decimal(s.offsetX), offsetY: decimal(s.offsetY), blur: decimal(s.blur), color: s.color.toString() })) };
        case 'Border':
            const border = value as IBorder;
            return { type: 'border', value: { thickness: decimal(border.thickness), style: border.type, color: border.color.toString() } };
        case 'Color':
            return { type: 'color', value: value.toString() as string };
        case 'Padding':
            const padding = value as IPadding;
            return { type: 'padding', value: { top: decimal(padding.top), left: decimal(padding.left), right: decimal(padding.right), bottom: decimal(padding.bottom) } };
    }
    throw new Error(`Did not recognize value.`);
}

function serializeKeyframes(property: string, keyframes: IKeyframe[]): ISerializedKeyframe<value.OneOfSerializedValue>[] {
    return keyframes.map((keyframe) => ({
        time: decimal(keyframe.time),
        value: serializeValue(property, keyframe.value),
        timingFunction: keyframe.timingFunction || null,
        transition: (keyframe.transition && keyframe.transition.template) || null
    }));
}

export const serializableKeyframeProperties = [
    'x',
    'y',
    'width',
    'height',
    'radius',
    'opacity',
    'originX',
    'originY',
    'rotationX',
    'rotationY',
    'rotationZ',
    'mirrorX',
    'mirrorY',
    'scaleX',
    'scaleY',
    'fill',
    'shadows',
    'border',
    'fitOption',
    'fitPositionX',
    'fitPositionY',
    'quality'
];

interface ISerializedElementIdType {
    id: string;
    type: N.NodeKind;
}

function serializeTransition(transition: N.ITransition): ISerializedTransition {
    const settings: ISerializedTransitionSetting[] = [];
    if (transition.settings) {
        for (const key in transition.settings) {
            const transitionSetting = transition.settings[key];
            settings.push({
                key,
                name: transitionSetting.name,
                value: transitionSetting.value,
            });
        }
    }
    return {
        name: transition.name,
        timingFunction: transition.timingFunction!,
        template: transition.template!,
        settings: settings,
    };
}
export function serializeDesignDocument(creative: N.CreativeDataNode, method?: OneOfMethod): string {
    let validateImageAssetId: (imageAssetId: string) => boolean;
    let validateDoc: (doc: ISerializedDocument) => void;

    creative = cloneDeep(creative);

    // Only do schema validation on saves
    const shouldValidate = method === 'create' || method === 'update';
    if (process.env.STUDIO_JS) {
        if (shouldValidate) {
            validateDoc = (doc: any): void => {
                const ajv = new Ajv({ allErrors: true });
                ajv.addFormat('image-asset-id', {
                    type: 'string',
                    validate: function (data: string): boolean {
                        return validateImageAssetId(data);
                    },
                });
                ajv.addFormat('color', {
                    type: 'string',
                    validate: function (data: string): boolean {
                        if (data.startsWith('linear-gradient')) {
                            const rgbaRegExp = 'rgba\\(\\s*(0|255|25[0-4]|2[0-4]\\d|1\\d\\d|0?\\d?\\d),(0|255|25[0-4]|2[0-4]\\d|1\\d\\d|0?\\d?\\d),(0|255|25[0-4]|2[0-4]\\d|1\\d\\d|0?\\d?\\d),(0?(\\.\\d+)?|1(\\.0)?)\\)';
                            const linearRegExp = new RegExp(`^linear\\-gradient\\(\\-?[0-9](\\.[0-9]+)?\\s\\-?[0-9](\\.[0-9]+)?\\s${rgbaRegExp}\\,\\-?[0-9](\\.[0-9]+)?\\s\\-?[0-9](\\.[0-9]+)?\\s${rgbaRegExp}\\)`, '');
                            return linearRegExp.test(data);
                        }
                        return RGBA_COLOR_PATTERN.test(data);
                    },
                });
                const validate = ajv.compile(schema());

                const valid = validate(doc);
                if (!valid) {
                    console.log(schema());
                    console.log(doc);
                    console.log('Invalid: ' + ajv.errorsText(validate.errors));
                    throw new Error('Invalid document:');
                }
            };
        }
    }

    const elements: OneOfSerializedElement[] = [];
    const serializedCreative: ISerializedCreative = {
        id: creative.id,
        width: creative.width,
        height: creative.height,
        fill: creative.fill.toString(),
        loops: creative.loops,
        radius: decimal(creative.radius),
        guidelines: creative.guidelines,
        preloadImage: creative.preloadImage,
        startTime: creative.startTime,
        stopTime: creative.stopTime,
        elements,
    };
    const images: ISerializedImageAsset[] = [];
    const asset = {
        images,
    };
    const elementIdTypes: ISerializedElementIdType[] = [];
    const head = {
        asset,
        elements: elementIdTypes,
    };
    const doc: ISerializedDocument = {
        head,
        body: {
            creative: serializedCreative
        },
    };
    for (const element of creative.elementIterator()) {
        elementIdTypes.push({ id: element.id, type: element.kind });
        const serializedElement = {} as OneOfSerializedElement;
        serializedElement.id = element.id;
        serializedElement.time = decimal(element.time);
        serializedElement.duration = decimal(element.duration);
        serializedElement.parentId = element.parentId || null;
        serializedElement.locked = element.locked;
        serializedElement.hidden = element.hidden;
        serializedElement.ratio = (element.ratio && decimal(element.ratio)) || null;
        if (element.inTransition) {
            serializedElement.inTransition = serializeTransition(element.inTransition);
        }
        if (element.outTransition) {
            serializedElement.outTransition = serializeTransition(element.outTransition);
        }

        const constraint = element.constraint;
        serializedElement.constraint = {
            auto: constraint.auto,
            contained: constraint.contained,
            top: constraint.top && decimal(constraint.top),
            left: constraint.left && decimal(constraint.left),
            right: constraint.right && decimal(constraint.right),
            bottom: constraint.bottom && decimal(constraint.bottom),
            width: constraint.width && decimal(constraint.width),
            height: constraint.height && decimal(constraint.height),
            ratio: constraint.ratio && decimal(constraint.ratio),
        };
        for (const property of serializableKeyframeProperties) {
            const propertyKeyframe = element[property];
            if (propertyKeyframe) {
                serializedElement[property] = serializeKeyframes(property, element[property]);
            }
        }
        switch (element.kind) {
            case N.NodeKind.Rectangle:
                const serializedRectangleElement = serializedElement as ISerializedRectangleElement;
                serializedRectangleElement.__rectangleKind = true;
                break;
            case N.NodeKind.Ellipse:
                const serializedEllipseElement = serializedElement as ISerializedEllipseElement;
                serializedEllipseElement.__ellipseKind = true;
                break;
            case N.NodeKind.Text:
            case N.NodeKind.Button:
                if (element.kind === N.NodeKind.Text) {
                    const serializedTextElement = serializedElement as ISerializedTextElement;
                    serializedTextElement.__textKind = true;
                }
                else {
                    const serializedButtonElement = serializedElement as ISerializedButtonElement;
                    serializedButtonElement.__buttonKind = true;
                }
                const serializedTextLikeElement = serializedElement as ISerializedTextElement | ISerializedButtonElement;
                const characterStyles: ISerializedCharacterStyle[] = [];
                for (const [id, style] of element.characterStyles.entries()) {
                    characterStyles.push({ id, value: serializeStyle2(style) });
                }
                serializedTextLikeElement.characterStyles = characterStyles.sort((a, b) => a.id.localeCompare(b.id));
                for (const property of N.textProperties) {
                    if (property === '__fontFamilyId') {
                        continue;
                    }
                    if (property === 'font') {
                        serializedTextLikeElement.font = (element.font && element.font.id) || element.__fontStyleId!;
                        continue;
                    }
                    const propertyKeyframe = element[property];
                    if (propertyKeyframe !== undefined) {
                        serializedTextLikeElement[property] = serializeKeyframes(property, propertyKeyframe) as any;
                    }
                }
                break;
            case N.NodeKind.Widget:
                const serializedWidgetElement = serializedElement as ISerializedWidgetElement;
                serializedWidgetElement.__widgetKind = true;
                const customProperties: value.ISerializedWidgetProperty[] = [];
                if (element.customProperties) {
                    for (const customProperty of element.customProperties) {
                        customProperties.push(value.serializeWidgetProperty(customProperty));
                    }
                }
                serializedWidgetElement.customProperties = customProperties;
                break;
            case N.NodeKind.Image:
                const serializedImageElement = serializedElement as ISerializedImageElement;
                serializedImageElement.__imageKind = true;
                if (element.imageAsset) {
                    const imageAsset = element.imageAsset!;
                    serializedImageElement.imageAssetId = imageAsset.id;
                    images.push({
                        id: imageAsset.id,
                        src: imageAsset.url,
                        width: imageAsset.width,
                        height: imageAsset.height
                    });
                }
                else if (element.feed) {
                    serializedImageElement.feed = element.feed;
                }
                else {
                    throw new Error('An image element must either have an image asset or feed.');
                }
                break;
        }
        elements.push(serializedElement);
    }
    validateImageAssetId = (imageAssetId: string): boolean => {
        for (const image of doc.head.asset.images) {
            if (image.id === imageAssetId) {
                return true;
            }
        }
        return false;
    };
    if (shouldValidate) {
        validateDoc!(doc);
    }
    return JSON.stringify(doc, null);
}

export function serializeStyle2(style: Partial<N.ICharacterProperties>): ISerializedCharacterProperties {
    const serializedStyle = {} as ISerializedCharacterProperties;
    for (const property in style) {
        const value = style[property];
        switch (property) {
            case '__fontFamilyId':
                continue;
            case 'font':
                if (typeof value === 'string') {
                    serializedStyle.font = value;
                }
                else {
                    serializedStyle.font = (value as IFontStyle).id;
                }
                break;
            case 'textColor':
                serializedStyle[property] = value.toString();
                break;
            case 'textShadows':
                serializedStyle[property] = (value as IShadow[]).map((v) => {
                    return { ...v, color: v.color.toString() };
                });
                break;
            case 'variable':
                const variable = value as IVariable;
                serializedStyle.variable = variable;
                break;
            default:
                serializedStyle[property] = value;
                break;
        }
    }
    return serializedStyle;
}

function deserializeStyle2(serializedStyle: ISerializedCharacterProperties): Partial<N.ICharacterProperties> {
    const style = {} as Partial<N.ICharacterProperties>;
    for (const property in serializedStyle) {
        switch (property) {
            case 'textColor':
                style[property] = Color.fromRGBA(serializedStyle[property]!);
                break;
            case 'textShadows':
                style[property] = serializedStyle[property]!.map((s) => {
                    return {
                        offsetX: s.offsetX,
                        offsetY: s.offsetY,
                        blur: s.blur,
                        color: Color.fromRGBA(s.color),
                    };
                });
                break;
            case 'variable':
                const variable = serializedStyle[property]!;
                style[property] = variable;
                break;
            default:
                style[property] = serializedStyle[property];
                break;
        }
    }
    return style;
}

export function cloneCreativeDocument(document: CreativeDataNode): CreativeDataNode {
    const serialized = serializeDesignDocument(document);
    const result = deserializeDesignDocument(serialized);
    return result;
}

export function deserializeKeyframePropertyValue(propertyName: string, value: string): IKeyframe[] | null {
    const parsed = JSON.parse(value);

    const data = parsed.map((keyframe) => {
        return {
            property: propertyName,
            value: deserializePropertyValue(propertyName, keyframe.value, value.indexOf('@') > -1),
            transition: keyframe.transition,
            time: keyframe.time,
            timingFunctions: keyframe.timingFunction
        };

    });
    return data;
}
export function serializeKeyframePropertyValue(propertyName: string, value: IKeyframe[]): string | undefined {
    if (value) {
        const data = value.map((keyframe) => {
            return {
                property: propertyName,
                value: serializePropertyValue(propertyName, keyframe.value),
                transition: keyframe.transition,
                time: keyframe.time,
                timingFunctions: keyframe.timingFunction
            };
        });

        return JSON.stringify(data);
    }
    return undefined;
}

/**
 * Clone value by serializing and deserializing. Compared to a deep clone this will also work with color properties
 * @param propertyName
 * @param value
 */
export function clonePropertyValue(propertyName: string, value: any): N.PropertyValue | undefined {
    return deserializePropertyValue(propertyName, serializePropertyValue(propertyName, value));
}

export function deserializePropertyValue(propertyName: N.OneOfElementPropertyKeys | N.WidgetPropertyKeys | string, value: any, isKeyFrame: boolean = false): N.PropertyValue | undefined {

    const unit = N.propertyToUnitMap[propertyName] || N.widgetPropertyToUnitMap[propertyName] || N.transitionPropertiesToUnitMap[propertyName];

    if (value === null) {
        value = undefined;
    }

    // Should maybe not affect all types?
    if (typeof value === 'string' && isKeyFrame) {
        return value;
    }
    switch (unit) {
        case 'angle':
            return parseFloat(value!) * Math.PI / 180;
        case 'integer':
            return parseInt(value, 10);
        case 'number':
        case 'px':
            return parseFloat(value!);
        case 'boolean':
            return value === 'true';
        case 'FontStyle':
            const fontStyle = value.split(' ') as IFontStyle;
            let src = fontStyle[3];
            try {
                // Support old data
                if (!isUrl(src)) {
                    if (typeof window !== 'undefined') {
                        src = atob(src);
                    }
                    else {
                        src = Buffer.from(src, 'base64').toString();
                    }
                }
            }
            catch { }

            return {
                id: fontStyle[0],
                weight: parseInt(fontStyle[1], 10),
                style: fontStyle[2],
                src,
                fontFamilyId: fontStyle[4],
            };
        case 'ImageAsset':
            const imageAsset = value.split(' ');
            return {
                id: imageAsset[0],
                url: imageAsset[1],
                width: imageAsset[2],
                height: imageAsset[3]
            };
        case 'Padding':
            const padding = value!.split(' ') || [];
            return {
                top: padding[0] ? parseFloat(padding[0]) : 0,
                right: padding[1] ? parseFloat(padding[1]) : 0,
                bottom: padding[2] ? parseFloat(padding[2]) : 0,
                left: padding[3] ? parseFloat(padding[3]) : 0,
            } as IPadding;
        case 'Border':
            const border = value!.split(' ');
            return border.length > 1 ? {
                thickness: parseInt(border[0], 10),
                type: border[1],
                color: border[2]
            } as IBorder : undefined;
        case 'Shadow[]':
            const shadows = value ? value!.split(/,(?![^\(]*\))/) : undefined;
            return shadows ? shadows.map(s => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    spread: parseInt(shadow[3], 10),
                    color: shadow[4]
                } as IShadow;
            }) : undefined;
        case 'TextShadow[]':
            if (Array.isArray(value)) {
                return value;
            }
            const textShadows = value ? value!.split(/,(?![^\(]*\))/) : undefined;
            return textShadows ? textShadows.map(s => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    color: shadow[3]
                } as ITextShadow;
            }) : undefined;
        case 'Color':
            return Color.parse(value!);
        case 'Feed':
        case 'variable':
            const feed = value.split(' ');
            return {
                id: feed[0],
                path: feed[1],
                step: {
                    start: parseInt(feed[2], 10),
                    size: parseInt(feed[3], 10),
                    occurrence: feed[4]
                },
                fallback: feed[5]
            } as IFeed;
        case 'string[]':
        case 'CustomProperty[]':
            let decodedValue;
            if (typeof window !== 'undefined') {
                decodedValue = JSON.parse(atob(value));
            }
            else {
                decodedValue = Buffer.from(value, 'base64').toString();
            }
            return decodedValue;
        case 'Transition':
            return JSON.parse(value);
        case 'text': {

            // Elements doesn't have "content" and value will instead be fetched from Version.properties
            if (value === undefined) {
                return undefined;
            }
            return deserializeVersionedText(value);
        }
        default:
            return value;
    }
}

/**
 * Serialize propertyValue to a string
 * @param propertyName
 * @param value
 */
export function serializePropertyValue(propertyName: string, value: any): string | undefined {
    const unit = N.propertyToUnitMap[propertyName] || N.widgetPropertyToUnitMap[propertyName] || N.transitionPropertiesToUnitMap[propertyName];
    switch (unit) {
        case 'angle':
            return `${Math.round(value * 180 / Math.PI)}`;
        case 'number':
            return `${value}`;
        case 'string':
            return value;
        case 'boolean':
            return value.toString();
        case 'FontStyle':
            const fontStyle = value as IFontStyle;
            const src = fontStyle.src;
            return `${fontStyle.id} ${fontStyle.weight} ${fontStyle.style} ${src} ${fontStyle.fontFamilyId}`;
        case 'ImageAsset':
            const imageAsset = value as N.IImageAsset;
            return `${imageAsset.id} ${imageAsset.url} ${imageAsset.width} ${imageAsset.height}`;
        case 'Border':
            const border = value as IBorder;
            return border ? `${border.thickness} ${border.type} ${border.color}` : '';
        case 'Padding':
            const padding = value as IPadding;
            return `${padding.top} ${padding.right || ''} ${padding.bottom || ''} ${padding.left || ''}`;
        case 'Shadow[]':
            const shadows = value as IShadow[];
            return shadows ? shadows.map(s => {
                return `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`;
            }).join(',') : '';
        case 'TextShadow[]':
            return value ? value.map((s) => {
                return `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color}`;
            }).join(',') : '';
        case 'Color':
            const color = value as Color;
            if (!color) {
                return '';
            } else {
                return color.toString();
            }
        case 'inline-styled-text':
            return serializeInlineStyledText(value);
        case 'text':
            return serializeVersionedText(value);
        case 'variable':
        case 'Feed':
            const feed = value as IFeed;
            return `${feed.id} ${feed.path} ${feed.step.start} ${feed.step.size} ${feed.step.occurrence} ${feed.fallback}`;
        case 'string[]':
        case 'CustomProperty[]':
            let encodedValue;
            if (typeof window !== 'undefined') {
                encodedValue = btoa(JSON.stringify(value));
            }
            else {
                encodedValue = Buffer.from(value).toString('base64');
            }
            return encodedValue;
        case 'Transition':
            return JSON.stringify(value);
        default:
            return value;
    }
}

export function deserializeVersionProperties(epv: IVersionProperty[] = []): IVersionProperty[] {
    const properties = epv.map(p => {
        const prop: any = {
            id: p.id as string,
            name: p.name as string,
            value: deserializePropertyValue(p.name, p.value)
        };
        return prop;
    });
    return properties;
}

export function serializeVersionProperties(vp: IVersionProperty[] = []): any[] {
    const properties = vp.map(p => ({
        id: p.id as string,
        name: p.name as string,
        value: serializePropertyValue(p.name, p.value)
    }));
    return properties;
}

